import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Bot, PowerOff } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAgentWorkflowStore } from "@/store/agentsStore";
import { CreateAgentModal } from "@/modules/agents/CreateAgentModal";
import { AgentEditorSheet } from "@/modules/agents/AgentEditorSheet";

export const AgentsTabController: React.FC = () => {
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLoadingAction, setIsLoadingAction] = useState<string | null>(null);
  const [editAgentId, setEditAgentId] = useState<string | null>(null);

  // Get agents from store
  const agentMap = useAgentWorkflowStore((state) => state.agentMap);
  const agentStateMap = useAgentWorkflowStore((state) => state.agentStateMap);
  const fetchAgents = useAgentWorkflowStore((state) => state.fetchAgents);
  const updateAgent = useAgentWorkflowStore((state) => state.updateAgent);
  const isLoadingAgents = useAgentWorkflowStore((state) => state.isLoadingList);

  // Convert agents map to array and sort by creation date
  const agentsList = React.useMemo(() => {
    return Array.from(agentMap.values()).sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [agentMap]);

  // Fetch agents on mount
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleDisableAgent = async (agentId: string) => {
    setIsLoadingAction(agentId);
    try {
      const updatedAgent = await updateAgent(agentId, {
        status: "DISABLED",
      });

      if (updatedAgent) {
        toast.success("Agent has been disabled");
      } else {
        throw new Error("Failed to disable agent");
      }
    } catch (_error) {
      toast.error("Failed to disable agent", {
        description: "Please try again later",
      });
    } finally {
      setIsLoadingAction(null);
    }
  };

  const handleViewAgent = (agentId: string) => {
    navigate(`/agents/${agentId}`);
  };

  const handleEditAgent = (agentId: string) => {
    setEditAgentId(agentId);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoadingAgents) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Agent
          </Button>
        </div>

        {agentsList.length === 0 ? (
          <div className="text-center py-12 bg-muted/50 rounded-lg">
            <Bot className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No agents yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first AI agent to get started
            </p>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Agent
            </Button>
          </div>
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {agentsList.map((agent) => {
                const agentState = agentStateMap.get(agent.id);
                const isLoading =
                  isLoadingAction === agent.id ||
                  agentState?.isLoadingUpdate ||
                  agentState?.isLoadingGet;

                return (
                  <div
                    key={agent.id}
                    className={`border rounded-lg p-4 space-y-3 transition-shadow hover:shadow-sm ${
                      agent.status === "DISABLED" ? "opacity-60" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-col gap-1 min-w-0 flex-1">
                        <span className="font-medium">{agent.name}</span>
                        {agent.description ? (
                          <span className="text-sm text-muted-foreground line-clamp-2">
                            {agent.description}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground italic">
                            No description provided
                          </span>
                        )}
                      </div>
                      <Badge
                        variant={
                          agent.status === "ACTIVE" ? "default" : "secondary"
                        }
                      >
                        {agent.status === "ACTIVE" ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>

                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>Created: {formatDate(agent.createdAt)}</span>
                      <span>Modified: {formatDate(agent.updatedAt)}</span>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleViewAgent(agent.id)}
                        disabled={isLoading}
                      >
                        View Details
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleEditAgent(agent.id)}
                        disabled={isLoading}
                      >
                        Edit Agent
                      </Button>
                    </div>
                    {agent.status === "ACTIVE" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-orange-600 hover:text-orange-600"
                        onClick={() => handleDisableAgent(agent.id)}
                        disabled={isLoading}
                      >
                        <PowerOff className="mr-2 h-4 w-4" />
                        Disable
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Create Agent Modal */}
      <CreateAgentModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSuccess={(agentId) => {
          setEditAgentId(agentId);
        }}
      />
      {editAgentId && (
        <AgentEditorSheet
          agentId={editAgentId}
          open
          onOpenChange={(open) => {
            if (!open) {
              setEditAgentId(null);
            }
          }}
        />
      )}
    </>
  );
};
